/**
 * Decorator that wraps an `IdentityProvider` so every acquire call emits
 * suffixed `auth.acquire_*_<source>` Forge events. Funnel denominator comes
 * from the `_started_` event; conversion from `_succeeded_` / `_failed_`.
 */
import { IdentitySource } from '@infiltre/shared';
import { type AnalyticsAuthSource, trackEvent } from '@/shared/lib/analytics';
import type { Identity, IdentityAcquireRequest, IdentityProvider } from './types';

const sourceFor = (provider: IdentityProvider): AnalyticsAuthSource =>
  provider.source === IdentitySource.Discord ? 'discord' : 'guest';

export const withAuthAnalytics = (provider: IdentityProvider): IdentityProvider => {
  const source = sourceFor(provider);
  return {
    ...provider,
    acquire: async (request?: IdentityAcquireRequest): Promise<Identity> => {
      trackEvent({ name: `auth.acquire_started_${source}`, data: {} });
      try {
        const identity = await provider.acquire(request);
        trackEvent({ name: `auth.acquire_succeeded_${source}`, data: {} });
        return identity;
      } catch (err) {
        const reason = err instanceof Error ? err.message : 'unknown_error';
        trackEvent({ name: `auth.acquire_failed_${source}`, data: { reason } });
        throw err;
      }
    },
  };
};
